import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import Navigation from './components/Navigation';
import LoadingScreen from './components/LoadingScreen';
import MobileWarning from './components/MobileWarning';
import Home from './pages/Home';
import Event from './pages/Event';
import Booth from './pages/Booth';
import Referee from './pages/Referee';
import Organization from './pages/Organization';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import SubAccount from './pages/SubAccount';
import ScoreScreen from './pages/ScoreScreen';
import NotFound from './pages/NotFound';
import { setInitializing, login } from './redux/slices/authSlice';
import request, { getToken } from '@/request';
import './App.css';

function AnimatedRoutes({ isLoggedIn }) {
  const location = useLocation();

  return (
    <ConfigProvider locale={zhCN}>
      <Routes location={location}>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={isLoggedIn ? <Home /> : <Navigate to="/login" />}
        />
        <Route
          path="/event"
          element={isLoggedIn ? <Event /> : <Navigate to="/login" />}
        />
        <Route
          path="/event/:id"
          element={isLoggedIn ? <Event /> : <Navigate to="/login" />}
        />
        <Route
          path="/booth"
          element={isLoggedIn ? <Booth /> : <Navigate to="/login" />}
        />
        <Route
          path="/referee"
          element={isLoggedIn ? <Referee /> : <Navigate to="/login" />}
        />
        <Route
          path="/organization"
          element={isLoggedIn ? <Organization /> : <Navigate to="/login" />}
        />
        <Route
          path="/change-password"
          element={isLoggedIn ? <ChangePassword /> : <Navigate to="/login" />}
        />
        <Route
          path="/sub-account"
          element={isLoggedIn ? <SubAccount /> : <Navigate to="/login" />}
        />
        <Route
          path="/score-screen"
          element={isLoggedIn ? <ScoreScreen /> : <Navigate to="/login" />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ConfigProvider>
  );
}

function App() {
  const dispatch = useDispatch();
  const { isLoggedIn, isInitializing } = useSelector((state) => state.auth);
  const [isMobile, setIsMobile] = useState(false);

  // Check if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      const isSmallScreen = window.innerWidth < 500;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // 检查是否有 token，有则获取用户信息
    const checkAuth = async () => {
      try {
        const token = getToken();

        if (token) {
          // 调用接口获取用户信息
          const userInfo = await request.get('/system/user');
          // 存储到 redux
          dispatch(login(userInfo));
        }

        dispatch(setInitializing(false));
      } catch (error) {
        console.error('Auth check failed:', error);
        // 如果接口调用失败，清除登录状态
        dispatch(setInitializing(false));
      }
    };

    checkAuth();
  }, [dispatch]);

  // Show loading screen while initializing
  if (isInitializing) {
    return <LoadingScreen />;
  }

  // Show mobile warning on mobile devices
  if (isMobile) {
    return <MobileWarning />;
  }

  return (
    <Router>
      {/* Don't show Navigation when user manually opens the /login page even if logged in */}
      <RouteAwareNavigation isLoggedIn={isLoggedIn} />
      <AnimatedRoutes isLoggedIn={isLoggedIn} />
    </Router>
  );
}

function RouteAwareNavigation({ isLoggedIn }) {
  const location = useLocation();
  // if the logged-in user manually navigates to /login, hide Navigation
  if (!isLoggedIn) return null;
  if (
    location &&
    (location.pathname === '/login' || location.pathname === '/score-screen')
  ) {
    return null;
  }
  return <Navigation />;
}

export default App;
